import React, { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, SearchIcon, UsersIcon, XIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { STAFF_MEMBERS, type InternalTeam } from '../../data/internalTeams';
interface InternalTeamFormDialogProps {
  open: boolean;
  /** Team being edited, or null when creating one. */
  team: InternalTeam | null;
  onClose: () => void;
  onSave: (team: InternalTeam) => void;
}

/** Create / edit an internal team: a name and a list of users. */
export function InternalTeamFormDialog({
  open,
  team,
  onClose,
  onSave
}: InternalTeamFormDialogProps) {
  const {
    t
  } = useLanguage();
  const [name, setName] = useState('');
  const [memberIds, setMemberIds] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    setName(team?.name ?? '');
    setMemberIds(team?.memberIds ?? []);
    setQuery('');
    setSubmitted(false);
    const id = window.setTimeout(() => nameRef.current?.focus(), 50);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, team, onClose]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return STAFF_MEMBERS;
    return STAFF_MEMBERS.filter(m => m.name.toLowerCase().includes(q) || m.role.toLowerCase().includes(q));
  }, [query]);
  const toggle = (id: string) => setMemberIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const nameError = submitted && !name.trim();
  const membersError = submitted && memberIds.length === 0;
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    if (!name.trim() || memberIds.length === 0) return;
    onSave({
      id: team?.id ?? `team-${Date.now()}`,
      name: name.trim(),
      memberIds
    });
  };
  return <AnimatePresence data-id="element-4092" data-mp="3fwjmefrjdllr">
      {open && <motion.div className="fixed inset-0 z-50 flex items-center justify-center p-4" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.15
    }} data-id="element-4093" data-mp="3fxl5n7ipheek">
          <div className="absolute inset-0 bg-slate-900/40" onClick={onClose} aria-hidden="true" data-id="element-4094" data-mp="2s6yzjls9i477" />
          <motion.form role="dialog" aria-modal="true" aria-labelledby="team-form-title" onSubmit={handleSubmit} className="relative w-full max-w-lg bg-white rounded-xl border border-slate-200 shadow-xl flex flex-col max-h-[85vh]" initial={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 8,
        scale: 0.98
      }} transition={{
        duration: 0.18,
        ease: 'easeOut'
      }} data-id="element-4095" data-mp="2w8klrxqoruu2">
            <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-slate-100" data-id="element-4096" data-mp="lo36aqjsbxf3">
              <h2 id="team-form-title" className="text-base font-semibold text-[#0F172A]" data-id="element-4097" data-mp="guqszckk3d1n">
                {team ? t('Modifier l’équipe', 'Edit team') : t('Nouvelle équipe', 'New team')}
              </h2>
              <button type="button" onClick={onClose} aria-label={t('Fermer', 'Close')} className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-id="element-4098" data-mp="7wycw670g1d7">
                <XIcon className="w-4 h-4" data-id="element-4099" data-mp="3q19tt15plhxd" />
              </button>
            </div>

            <div className="px-6 py-5 space-y-5 overflow-y-auto" data-id="element-4100" data-mp="1sb8ewiqj1xa1">
              <div data-id="element-4101" data-mp="1safhi8dgkk4k">
                <label htmlFor="team-name" className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-4102" data-mp="1s8vzodc5m10o">
                  {t('Nom de l’équipe', 'Team name')}
                </label>
                <input ref={nameRef} id="team-name" value={name} onChange={e => setName(e.target.value)} placeholder={t('Ex. : Cellule de mesures d’urgence', 'e.g. Emergency response unit')} aria-invalid={nameError} className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-100 ${nameError ? 'border-red-400' : 'border-slate-200 focus:border-[#4F46E5]'}`} data-id="element-4103" data-mp="1vdmmi9t620k0" />
                {nameError && <p className="text-xs text-red-600 mt-1" data-id="element-4104" data-mp="17n8u2i6o5zow">{t('Le nom est requis.', 'Name is required.')}</p>}
              </div>

              <div data-id="element-4105" data-mp="toj0hcj26i5a">
                <div className="flex items-baseline justify-between mb-1.5" data-id="element-4106" data-mp="to9iab499fa5">
                  <span className="text-sm font-medium text-slate-700" data-id="element-4107" data-mp="ylj6jnfwrkwe">{t('Membres', 'Members')}</span>
                  <span className="text-xs text-slate-400" data-id="element-4108" data-mp="f1k7ve8gthoz">
                    {memberIds.length} {t('sélectionné(s)', 'selected')}
                  </span>
                </div>
                <div className="relative mb-2" data-id="element-4109" data-mp="o1pg1qlhgk47">
                  <SearchIcon className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-4110" data-mp="nzcnkji3mrcw" />
                  <input value={query} onChange={e => setQuery(e.target.value)} placeholder={t('Rechercher un utilisateur', 'Search a user')} aria-label={t('Rechercher un utilisateur', 'Search a user')} className="w-full border border-slate-200 rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5]" data-id="element-4111" data-mp="k28akx623mz1" />
                </div>
                <ul className={`border rounded-lg divide-y divide-slate-100 max-h-64 overflow-y-auto ${membersError ? 'border-red-400' : 'border-slate-200'}`} data-id="element-4112" data-mp="1gqwaoehtgpk6">
                  {filtered.length === 0 && <li className="px-3 py-6 text-center text-sm text-slate-400" data-id="element-4113" data-mp="edrt3t9rpgka">
                      {t('Aucun utilisateur trouvé.', 'No user found.')}
                    </li>}
                  {filtered.map(member => {
                const checked = memberIds.includes(member.id);
                return <li key={member.id} data-id="element-4114" data-mp="s67r2fntaoec">
                        <label className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50" data-id="element-4115" data-mp="x2exe9k4e1wt">
                          <input type="checkbox" checked={checked} onChange={() => toggle(member.id)} className="sr-only" data-id="element-4116" data-mp="123cdmglkklvo" />
                          <span className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${checked ? 'bg-[#4F46E5] border-[#4F46E5]' : 'border-slate-300 bg-white'}`} aria-hidden="true" data-id="element-4117" data-mp="37v991qdluz4w">
                            {checked && <CheckIcon className="w-3 h-3 text-white" data-id="element-4118" data-mp="2qm6r750971ft" />}
                          </span>
                          <span className="w-7 h-7 rounded-full bg-slate-100 text-slate-600 text-[11px] font-semibold flex items-center justify-center flex-shrink-0" data-id="element-4119" data-mp="2mi8suwir5unt">
                            {initials(member.name)}
                          </span>
                          <span className="min-w-0" data-id="element-4120" data-mp="3a94qwbdsxm3i">
                            <span className="block text-sm font-medium text-slate-800 truncate" data-id="element-4121" data-mp="3abqyuc4lu3m9">
                              {member.name}
                            </span>
                            <span className="block text-xs text-slate-500 truncate" data-id="element-4122" data-mp="1ywbzj1oxaznz">{member.role}</span>
                          </span>
                        </label>
                      </li>;
              })}
                </ul>
                {membersError && <p className="text-xs text-red-600 mt-1" data-id="element-4123" data-mp="1uwjbet4hibr">
                    {t('Ajoutez au moins un membre.', 'Add at least one member.')}
                  </p>}
              </div>
            </div>

            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-100" data-id="element-4124" data-mp="3oz2sohr8vwj3">
              <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-200 rounded-lg hover:bg-slate-50" data-id="element-4125" data-mp="gjsxltlrvrkt">
                {t('Annuler', 'Cancel')}
              </button>
              <button type="submit" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-indigo-700" data-id="element-4126" data-mp="bqgbbq4kfwv8">
                <UsersIcon className="w-4 h-4" data-id="element-4127" data-mp="14aeucoqbtpfi" />
                {team ? t('Enregistrer', 'Save') : t('Créer l’équipe', 'Create team')}
              </button>
            </div>
          </motion.form>
        </motion.div>}
    </AnimatePresence>;
}
function initials(name: string): string {
  return name.split(' ').map(part => part[0]).slice(0, 2).join('').toUpperCase();
}